'use client';

import { useLocale } from 'next-intl';
import {
  Pencil,
  Trash2,
  UserPlus,
  Shield,
  Users,
  Copy,
  Loader2
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PermissionGate } from '@/features/auth';
import { RoleColorBadge } from './role-color-badge';
import { getRoleDisplayName } from '../mappers/role.mapper';
import type { Role } from '../types';

interface RoleCompactRowProps {
  role: Role;
  onAssign: (role: Role) => void;
  onEdit: (role: Role) => void;
  onDelete: (role: Role) => void;
  onDuplicate: (role: Role) => void;
  onSelectRole: (role: Role) => void;
  isDuplicating?: boolean;
}

export function RoleCompactRow({
  role,
  onAssign,
  onEdit,
  onDelete,
  onDuplicate,
  onSelectRole,
  isDuplicating
}: RoleCompactRowProps) {
  const locale = useLocale();

  return (
    <div className='flex items-center gap-3 rounded-md border px-4 py-2.5'>
      <div className='min-w-0 flex-1'>
        <RoleColorBadge
          name={getRoleDisplayName(role, locale)}
          color={role.color}
          onClick={() => onSelectRole(role)}
        />
      </div>

      <div className='flex items-center gap-3'>
        <Badge variant='outline' className='gap-1'>
          <Shield className='h-3 w-3' />
          {role.permissions.length}
        </Badge>
        <Badge variant='outline' className='gap-1'>
          <Users className='h-3 w-3' />
          {role.users_count}
        </Badge>
      </div>

      <div className='flex shrink-0 items-center gap-1'>
        <PermissionGate permissions={['roles.create']}>
          <Button
            variant='ghost'
            size='icon'
            className='h-7 w-7'
            onClick={() => onDuplicate(role)}
            disabled={isDuplicating}
          >
            {isDuplicating ? (
              <Loader2 className='h-3.5 w-3.5 animate-spin' />
            ) : (
              <Copy className='h-3.5 w-3.5' />
            )}
          </Button>
        </PermissionGate>
        <PermissionGate permissions={['roles.edit']}>
          <Button
            variant='ghost'
            size='icon'
            className='h-7 w-7'
            onClick={() => onAssign(role)}
          >
            <UserPlus className='h-3.5 w-3.5' />
          </Button>
          {role.can_edit && (
            <Button
              variant='ghost'
              size='icon'
              className='h-7 w-7'
              onClick={() => onEdit(role)}
            >
              <Pencil className='h-3.5 w-3.5' />
            </Button>
          )}
        </PermissionGate>
        {role.can_delete && (
          <PermissionGate permissions={['roles.delete']}>
            <Button
              variant='ghost'
              size='icon'
              className='h-7 w-7'
              onClick={() => onDelete(role)}
            >
              <Trash2 className='text-destructive h-3.5 w-3.5' />
            </Button>
          </PermissionGate>
        )}
      </div>
    </div>
  );
}
